import * as API from '@api';
import { type SendingRequestBundle, SendingsDashboardType } from '@domain/types';
import { tracking } from '@services/analytics';
import { realtimeClient } from '@services/realtimeClient.service';
import { realtimeCommunicationRequestUpdated } from '@store/externalEvents/externalEvents.action';
import { resolveTerminalImportStatus } from '../common/document-import/resolveTerminalImportStatus';
import type { TerminalImportStatus } from '../common/document-import/uploadStatus';
import { notifyImportAnalysisComplete } from './analytics/fileImportCompletedTracker';

const POLLING_INTERVALS_NORMAL = [15_000, 30_000, 60_000, 120_000, 240_000];
const POLLING_INTERVALS_AGGRESSIVE = [15_000, 25_000, 40_000, 65_000, 100_000];
const DISCONNECTED_STATES = new Set(['disconnected', 'unavailable', 'failed']);

interface PollerConfig {
  dispatch: (action: unknown) => void;
}

interface PendingTimer {
  timer: ReturnType<typeof setTimeout>;
  attempt: number;
  startedAt: number;
  scheduledAt: number;
}

function isDocumentAnalysisComplete(bundle: SendingRequestBundle): boolean {
  return resolveTerminalImportStatus(bundle) !== undefined;
}

export class AnalysisFallbackPoller {
  private pendingTimers = new Map<string, PendingTimer>();
  private processedDocuments = new Set<string>();
  private lastFetchedBundles = new Map<string, SendingRequestBundle>();
  private connectionMode: 'normal' | 'aggressive' = 'normal';
  private disconnectedAt: number | null = null;
  private dispatch: ((action: unknown) => void) | null = null;
  private unbindConnectionState: (() => void) | null = null;

  init(config: PollerConfig) {
    this.dispatch = config.dispatch;

    try {
      this.unbindConnectionState = realtimeClient.bindConnectionStateChange(({ current }) => {
        this.handleConnectionStateChange(current);
      });
    } catch (_error) {}
  }

  destroy() {
    this.pendingTimers.forEach(({ timer }) => clearTimeout(timer));
    this.pendingTimers.clear();
    this.lastFetchedBundles.clear();
    this.unbindConnectionState?.();
  }

  setConnectionMode(mode: 'normal' | 'aggressive') {
    this.connectionMode = mode;
  }

  startPolling(documentId: string) {
    if (this.processedDocuments.has(documentId)) return;
    if (this.pendingTimers.has(documentId)) return;
    this.scheduleAttempt(documentId, 0, Date.now());
  }

  stopPolling(documentId: string) {
    const pending = this.pendingTimers.get(documentId);
    if (pending === undefined && !this.processedDocuments.has(documentId)) return;
    if (pending !== undefined) clearTimeout(pending.timer);
    this.pendingTimers.delete(documentId);
    this.lastFetchedBundles.delete(documentId);
    this.processedDocuments.add(documentId);
  }

  private handleConnectionStateChange(state: string) {
    if (DISCONNECTED_STATES.has(state)) {
      if (this.connectionMode !== 'aggressive') {
        this.connectionMode = 'aggressive';
        this.rescheduleActiveTimers();
      }
      if (this.disconnectedAt === null) {
        this.disconnectedAt = Date.now();
        tracking.trackPusherDisconnected({ state });
      }
    } else if (state === 'connected' && this.disconnectedAt !== null) {
      this.connectionMode = 'normal';
      tracking.trackPusherReconnected({ downtimeMs: Date.now() - this.disconnectedAt });
      this.disconnectedAt = null;
    }
  }

  private rescheduleActiveTimers() {
    const entries = [...this.pendingTimers.entries()];
    entries.forEach(([documentId, { timer, attempt, startedAt, scheduledAt }]) => {
      clearTimeout(timer);
      this.pendingTimers.delete(documentId);

      if (attempt >= POLLING_INTERVALS_AGGRESSIVE.length) return;

      const elapsed = Date.now() - scheduledAt;
      const remainingMs = Math.max(0, POLLING_INTERVALS_AGGRESSIVE[attempt] - elapsed);
      this.setTimer(documentId, attempt, startedAt, remainingMs);
    });
  }

  private scheduleAttempt(documentId: string, attempt: number, startedAt: number) {
    const intervals =
      this.connectionMode === 'normal' ? POLLING_INTERVALS_NORMAL : POLLING_INTERVALS_AGGRESSIVE;

    if (attempt >= intervals.length) {
      this.handleGiveUp(documentId).catch(() => {});
      return;
    }

    this.setTimer(documentId, attempt, startedAt, intervals[attempt]);
  }

  private setTimer(documentId: string, attempt: number, startedAt: number, delayMs: number) {
    const scheduledAt = Date.now();
    const timer = setTimeout(() => {
      this.runAttempt(documentId, attempt, startedAt);
    }, delayMs);
    this.pendingTimers.set(documentId, { timer, attempt, startedAt, scheduledAt });
  }

  private emitGiveUpRecovery(
    documentId: string,
    terminalStatus: TerminalImportStatus,
    bundle: SendingRequestBundle,
  ): void {
    notifyImportAnalysisComplete(documentId, terminalStatus, 'poller_give_up');
    this.dispatch?.(
      realtimeCommunicationRequestUpdated({
        resource: { id: documentId },
        prefetchedBundle: bundle,
      }),
    );
    this.stopPolling(documentId);
  }

  private async handleGiveUp(documentId: string): Promise<void> {
    const lastBundle = this.lastFetchedBundles.get(documentId);
    const fromLastFetch = lastBundle ? resolveTerminalImportStatus(lastBundle) : undefined;

    if (fromLastFetch && lastBundle) {
      this.emitGiveUpRecovery(documentId, fromLastFetch, lastBundle);
      return;
    }

    try {
      const bundle: SendingRequestBundle = await API.fetchSendingRequest(
        documentId,
        SendingsDashboardType.Draft,
        true,
      );
      const fromFinalFetch = resolveTerminalImportStatus(bundle);
      if (fromFinalFetch) {
        this.emitGiveUpRecovery(documentId, fromFinalFetch, bundle);
        return;
      }
    } catch (_error) {}

    tracking.trackAnalysisPollingGaveUp({ documentId });
    this.lastFetchedBundles.delete(documentId);
    this.pendingTimers.delete(documentId);
    this.processedDocuments.add(documentId);
  }

  private async runAttempt(documentId: string, attempt: number, startedAt: number) {
    if (this.processedDocuments.has(documentId)) return;

    try {
      const bundle: SendingRequestBundle = await API.fetchSendingRequest(
        documentId,
        SendingsDashboardType.Draft,
        true,
      );

      this.lastFetchedBundles.set(documentId, bundle);

      if (!isDocumentAnalysisComplete(bundle)) {
        this.scheduleAttempt(documentId, attempt + 1, startedAt);
        return;
      }

      this.stopPolling(documentId);
      tracking.trackPusherEventMissed({ documentId, detectedAfterMs: Date.now() - startedAt });

      this.dispatch?.(
        realtimeCommunicationRequestUpdated({
          resource: { id: documentId },
          prefetchedBundle: bundle,
        }),
      );
    } catch (_error) {
      this.scheduleAttempt(documentId, attempt + 1, startedAt);
    }
  }
}

export const analysisFallbackPoller = new AnalysisFallbackPoller();
